import { Button, Stack, Typography, useTheme } from '@mui/material';
import { DynamicIcon } from 'src/components';
import {
  useComposerBanners,
  useReportBannerVisible,
} from 'src/pages/Conversations/Chat/Banners/composer-banners-context';
import { ChatInfoBackground } from 'src/pages/Conversations/Chat/ChatInfo/chat-info';
import { getBannerActionSx } from 'src/pages/Conversations/Chat/shared-styles';
import { AiGenerationError, AiGenerationErrorKind } from './ai-generation';

export interface AiGenerationErrorBarProps {
  error: AiGenerationError | null;
  /** Macro or prompt name to show alongside the message, matching the pending bar. */
  sourceName: string;
  /** Ignored for non-retryable failures, which offer dismissal only. */
  onRetry?: () => void;
  onDismiss: () => void;
}

/**
 * The failure counterpart to `PendingAiSuggestionHeader`: same slot, same banner
 * key, same chrome, so a failed generation reads as the strip the accept /
 * refine / dismiss bar occupies rather than a floating alert. The two never show
 * together — the composer stands the pending bar down while an error is up.
 */
export const AiGenerationErrorBar = ({
  error,
  sourceName,
  onRetry,
  onDismiss,
}: AiGenerationErrorBarProps) => {
  const theme = useTheme();
  const { hasBannerBelow } = useComposerBanners();

  useReportBannerVisible('ai', !!error);

  if (!error) return null;

  const canRetry = Boolean(onRetry) && error.kind === AiGenerationErrorKind.Retryable;
  const ink = theme.palette.custom.OV.sys.error;

  return (
    <ChatInfoBackground
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      textColor={ink}
      bleed={hasBannerBelow('ai')}
    >
      <Stack direction="row" alignItems="center" color={ink} gap={1} sx={{ minWidth: 0 }}>
        <DynamicIcon name="error_outline" sx={{ justifyContent: 'center', lineHeight: 1 }} />
        <Typography sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
          {sourceName ? <strong>{sourceName}: </strong> : null}
          {error.message}
        </Typography>
      </Stack>
      <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
        <Button variant="text" size="small" onClick={onDismiss} sx={getBannerActionSx}>
          <DynamicIcon name="clear" /> Dismiss
        </Button>
        {canRetry ? (
          <Button variant="text" size="small" onClick={onRetry} sx={getBannerActionSx}>
            <DynamicIcon name="refresh" /> Try again
          </Button>
        ) : null}
      </Stack>
    </ChatInfoBackground>
  );
};
